import { readCsrfToken } from "@/lib/csrf"
import i18n from "@/i18n"

export {
  GRIP_DRAG_MIN_HEIGHT_CM,
  MAX_HEIGHT_CM,
  MIN_HEIGHT_CM,
  PROP_GRIP_DRAG_MIN_HEIGHT_CM,
} from "./scale-sheet-height.ts"
export const SLIDER_MAX_HEIGHT_CM = 250

export type ScaleSheetCharacterRow = {
  id: number
  name: string
  saved_height_cm: number | null
  effective_height_cm: number
  scale_pose_url: string | null
  can_generate_scale_pose?: boolean
  image_width?: number
  image_height?: number
}

export type ScaleSheetResponse = {
  ok: boolean
  status: string
  character_ids_key: string
  image_url: string | null
  image_download_url?: string | null
  layout_meta: {
    figures?: {
      character_id: number
      name: string
      height_cm: number
      x: number
      y: number
      w: number
      h: number
      scale_pose_url?: string | null
      hit_rect?: { x: number; y: number; w: number; h: number }
      image_width?: number
      image_height?: number
    }[]
    content_width_preview?: number
    px_per_cm_preview?: number
    characters?: {
      id: number
      image_width?: number
      image_height?: number
    }[]
  } | null
  scale_unit: string
  characters: ScaleSheetCharacterRow[]
  error?: string
}

export async function fetchScaleSheet(
  projectId: number,
  characterIds?: number[],
  options?: { preview?: boolean; shotId?: number; editOnly?: boolean },
): Promise<ScaleSheetResponse> {
  const params = new URLSearchParams()
  if (options?.shotId != null && options.shotId > 0) {
    params.set("shot_id", String(options.shotId))
  } else if (characterIds && characterIds.length > 0) {
    params.set("character_ids", characterIds.join(","))
  }
  if (options?.preview) {
    params.set("preview", "1")
  }
  if (options?.editOnly) {
    params.set("edit_only", "1")
  }
  const qs = params.toString()
  const res = await fetch(`/projects/${projectId}/workspace/scale_sheet${qs ? `?${qs}` : ""}`, {
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": readCsrfToken() },
  })
  const data = (await res.json()) as ScaleSheetResponse
  if (!res.ok || !data.ok) {
    throw new Error(data.error ?? i18n.t("workspace:charactersPage.scaleSheetLoadError"))
  }
  return data
}

export type EnsureShotScaleSheetResponse = {
  ok: boolean
  status: string
  character_ids_key: string | null
  error?: string
}

export async function ensureShotScaleSheet(
  projectId: number,
  shotId: number,
): Promise<Pick<EnsureShotScaleSheetResponse, "status" | "character_ids_key">> {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/ensure_scale_sheet`, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": readCsrfToken() },
  })
  const data = (await res.json()) as EnsureShotScaleSheetResponse
  if (!res.ok || !data.ok) {
    throw new Error(data.error ?? i18n.t("workspace:charactersPage.scaleSheetLoadError"))
  }
  return { status: data.status, character_ids_key: data.character_ids_key }
}

export async function patchCharacterHeights(
  projectId: number,
  updates: { character_id: number; height_cm: number }[],
): Promise<void> {
  const res = await fetch(`/projects/${projectId}/workspace/character_heights`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": readCsrfToken(),
    },
    body: JSON.stringify({ updates }),
  })
  const data = (await res.json()) as { ok?: boolean; error?: string }
  if (!res.ok || !data.ok) {
    throw new Error(data.error ?? i18n.t("workspace:charactersPage.scaleSheetSaveError"))
  }
}
